/**
 * Hook that provides dynamic sidebar items (agents, workspaces, boards)
 * for searching in ModulePickerContent.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgents } from '../../modules/ai_agents/hooks/useAgents';
import { useWorkspaces } from '../../modules/tasks/hooks/useWorkspaces';
import { useBoards, useSharedBoards } from '../../modules/tasks/hooks/useBoards';
import type { DynamicPickerItem } from './ModulePickerContent';

export function useModulePickerDynamicItems(): DynamicPickerItem[] {
  const { t } = useTranslation('common');

  const { data: agentsData } = useAgents({ is_active: true });
  const { data: workspacesData } = useWorkspaces();
  const { data: boardsData } = useBoards();
  const { data: sharedBoardsData } = useSharedBoards();

  return useMemo(() => {
    const agents = agentsData?.data || [];
    const workspaces = workspacesData?.data || [];
    const boards = boardsData?.data || [];
    const sharedBoards = sharedBoardsData?.data || [];
    const items: DynamicPickerItem[] = [];

    const agentsCategory = t('entities.agents');
    for (const agent of agents) {
      items.push({
        label: agent.name,
        path: `/ai-agents/${agent.id}`,
        moduleId: 'ai',
        category: agentsCategory,
      });
    }

    const workspacesCategory = t('entities.workspaces');
    for (const ws of workspaces) {
      items.push({
        label: ws.name,
        path: `/tasks/workspaces/${ws.id}`,
        moduleId: 'tasks',
        category: workspacesCategory,
      });
    }

    const boardsCategory = t('entities.boards');
    const allBoards = [...boards, ...sharedBoards];
    const seenBoardIds = new Set<string>();
    for (const board of allBoards) {
      if (seenBoardIds.has(board.id)) continue;
      seenBoardIds.add(board.id);
      items.push({
        label: board.name,
        path: `/tasks/boards/${board.id}`,
        moduleId: 'tasks',
        category: boardsCategory,
      });
    }

    return items;
  }, [agentsData, workspacesData, boardsData, sharedBoardsData, t]);
}
